import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { type ProposalOutput } from '@1financial/shared';
import { fmtDate as fmtDateStd } from '../lib/date.js';
import { DocumentBranding } from '../components/DocumentBranding.js';

function fmtDate(d: string | null | undefined) {
  if (!d) return null;
  return fmtDateStd(d);
}

export function PublicProposalPage() {
  const { token } = useParams<{ token: string }>();
  const [actioned, setActioned] = useState<'accepted' | 'negotiated' | null>(null);

  const detail = useQuery<ProposalOutput>({
    queryKey: ['public-proposal', token],
    queryFn: async () => {
      const res = await fetch(`/v1/proposals/public/${token}`);
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: ProposalOutput };
      return body.data;
    },
  });

  const accept = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/v1/proposals/accept/${token}`, { method: 'POST' });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      return res.json();
    },
    onSuccess: () => setActioned('accepted'),
  });

  const negotiate = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/v1/proposals/negotiate/${token}`, { method: 'POST' });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      return res.json();
    },
    onSuccess: () => setActioned('negotiated'),
  });

  if (detail.isLoading) return <div className="p-8 text-sm text-muted">Loading proposal…</div>;
  if (detail.error) return <div className="p-8 text-sm text-rose-300">Error: {detail.error.message}</div>;
  if (!detail.data) return null;

  const p = detail.data;
  const alreadyActioned = p.status === 'SIGNED' || p.status === 'DECLINED' || p.status === 'EXPIRED';
  const allComplimentary = p.pricingMode !== 'LUMP_SUM' && p.items.length > 0 && p.items.every((i) => i.unitPrice === 0);

  return (
    <div className="min-h-screen bg-bg-base flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-2xl bg-bg-panel border border-border rounded-xl p-6 sm:p-8 space-y-6">
        <DocumentBranding branding={p.branding} />

        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-white">{p.subject}</h1>
          <p className="text-sm text-muted mt-1">
          {p.type === 'RECURRING' ? 'Recurring services proposal for your review and signature.' : 'Proposal for your review and signature.'}
          {!allComplimentary && p.feeAmount != null && p.feeCurrency && (
            <span className="block mt-1 text-white">
              {p.type === 'RECURRING'
                ? `Fee: ${p.feeCurrency} ${(p.feeAmount / 100).toFixed(2)} per ${({ MONTHLY: 'month', QUARTERLY: 'quarter', SEMI_ANNUAL: '6 months', ANNUAL: 'year' } as Record<string, string>)[p.billingSchedule ?? ''] ?? 'period'}`
                : `Fee: ${p.feeCurrency} ${(p.feeAmount / 100).toFixed(2)}`}
            </span>
          )}
         {p.expiresAt && (
           <span className="block mt-1 text-amber-300">Expires on {fmtDate(p.expiresAt)}</span>
         )}
          {!p.expiresAt && (
            <span className="block mt-1 text-amber-300">No expiry set</span>
          )}
        </p>
        </div>

        {p.body.trim() && (
          <div className="bg-bg-card border border-border rounded-lg p-4 text-sm text-white whitespace-pre-wrap break-words">
            {p.body}
          </div>
        )}

        {p.items.length > 0 && (() => {
          // Complimentary rows (price 0) print "Included", never 0.00; an
          // all-complimentary proposal renders as the price-less scope list
          // (no price cells, no total) exactly like LUMP_SUM.
          const hidePrices = p.pricingMode === 'LUMP_SUM' || allComplimentary;
          return (
          <div>
            <h3 className="text-sm font-medium text-white mb-2">
              {hidePrices
                ? 'Scope of services — everything below is included'
                : (p.type === 'RECURRING' ? 'Services & recurring fees' : 'Services & fees')}
            </h3>
            <div className="bg-bg-card border border-border rounded-lg divide-y divide-border">
              {p.items.map((item, idx) => (
                <div key={item.id} className="flex items-start justify-between gap-4 px-4 py-2.5">
                  <div className="min-w-0">
                    <div className="text-sm text-white"><span className="text-muted mr-2">{idx + 1}.</span>{item.title}</div>
                    {item.description && <div className="text-xs text-muted mt-0.5 ml-6">{item.description}</div>}
                  </div>
                  {!hidePrices && (
                    item.unitPrice === 0
                      ? <div className="text-sm text-emerald-300 shrink-0">Included</div>
                      : <div className="text-sm text-white shrink-0">{item.currency} {(item.unitPrice / 100).toFixed(2)}</div>
                  )}
                </div>
              ))}
              {!allComplimentary && p.feeAmount != null && p.feeCurrency && (
                <div className="flex justify-between px-4 py-2.5 text-sm font-medium text-white">
                  <span>Total</span>
                  <span>{p.feeCurrency} {(p.feeAmount / 100).toFixed(2)}</span>
                </div>
              )}
            </div>
            {p.type === 'RECURRING' && p.billingSchedule && (
              <p className="text-xs text-muted mt-2">
                Billed {({ MONTHLY: 'monthly', QUARTERLY: 'quarterly', SEMI_ANNUAL: 'every six months', ANNUAL: 'annually' } as Record<string, string>)[p.billingSchedule] ?? p.billingSchedule}
                {p.billingStartDate ? ` from ${fmtDate(p.billingStartDate)}` : ''}.
              </p>
            )}
          </div>
          );
        })()}

        {p.clauses.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-white mb-2">Terms & clauses</h3>
            <div className="bg-bg-card border border-border rounded-lg p-4 space-y-3">
              {p.clauses.map((clause, idx) => (
                <div key={clause.id}>
                  <div className="text-sm font-medium text-white">{idx + 1}. {clause.title}</div>
                  <div className="text-xs text-muted mt-1 whitespace-pre-wrap break-words">{clause.body}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <h3 className="text-sm font-medium text-white mb-2">Recipients</h3>
          <div className="space-y-1">
            {p.recipients.map((r, i) => (
              <div key={i} className="text-sm text-muted break-words">{r.name} · {r.email}</div>
            ))}
          </div>
        </div>

        {actioned === 'accepted' || p.status === 'SIGNED' ? (
          <div className="text-center text-emerald-300 font-medium">Thank you — this proposal has been accepted.</div>
        ) : actioned === 'negotiated' || p.status === 'SENT_BACK' ? (
          <div className="text-center text-amber-300 font-medium">
            Thank you — your negotiation request has been sent to the 1Financial team.
          </div>
        ) : alreadyActioned ? (
          <div className="text-center text-muted">This proposal is no longer available for action.</div>
        ) : (
          <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
            <button
              onClick={() => negotiate.mutate()}
              disabled={negotiate.isPending}
              className="px-4 py-2 rounded-lg text-sm sm:text-base bg-bg-card border border-border text-white hover:bg-bg-hover w-full sm:w-auto min-h-[44px]"
            >
              Negotiate
            </button>
            <button
              onClick={() => accept.mutate()}
              disabled={accept.isPending}
              className="px-4 py-2 rounded-lg text-sm sm:text-base bg-accent hover:bg-emerald-400 text-accent-fg font-medium w-full sm:w-auto min-h-[44px]"
            >
              Accept & Sign
            </button>
          </div>
        )}

        {accept.error && <div className="text-sm text-rose-300">{accept.error.message}</div>}
        {negotiate.error && <div className="text-sm text-rose-300">{negotiate.error.message}</div>}
        {p.branding?.footerNote && <p className="text-xs text-muted whitespace-pre-wrap break-words border-t border-border pt-4">{p.branding.footerNote}</p>}
      </div>
    </div>
  );
}
